import { useState, useEffect, useCallback } from 'react';
import { API_TIMEOUT, buildApiUrl } from '../config';

const apiRequest = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options
  };
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.mensaje || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      data,
      error: data.error,
      mensaje: data.mensaje
    };
    
  } catch (error) {
    throw error;
  }
};

export const useClientes = (options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const {
    immediate = false
  } = options;

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(buildApiUrl('clientes'), {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const validatedData = result.data || result;
      setData(validatedData);
      return validatedData;
    } catch (err) {
      const errorMessage = err.name === 'AbortError' 
        ? 'Tiempo de espera agotado' 
        : 'Error de conexión con el servidor';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteCliente = useCallback(async (phone) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiRequest('cliente', {
        method: 'DELETE',
        body: JSON.stringify({ phone })
      });

      if (result.success && result.error === 0) {
        setData(prev => prev.filter(item => item.Phone !== phone));
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createCliente = useCallback(async (clienteData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiRequest('clientes', {
        method: 'POST',
        body: JSON.stringify(clienteData)
      });

      if (result.success) {
        await fetchClientes();
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchClientes]);

  const updateCliente = useCallback(async (id, clienteData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiRequest(`clientes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(clienteData)
      });

      if (result.success) {
        setData(prev => prev.map(item => 
          item.IdCliente === id ? { ...item, ...clienteData } : item
        ));
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchClientes();
    }
  }, [immediate, fetchClientes]);

  return {
    data,
    loading,
    error,
    selectedId,
    setSelectedId,
    fetchClientes,
    refetch: fetchClientes,
    deleteCliente,
    createCliente,
    updateCliente
  };
};

export const deleteCliente = async (phone) => {
  return apiRequest('cliente', {
    method: 'DELETE',
    body: JSON.stringify({ phone })
  });
};

export const getClientesConTarjetas = async () => {
  return apiRequest('clientes/tarjetas', { method: 'GET' });
};

export default useClientes;
